import React, { useState, useEffect } from 'react';
import { X, UserCircle } from 'lucide-react';
import { adminApi } from '../../../services/admin/adminApi';
import type { User, UpdateUserRequest } from '../../../services/admin/types';
import { planManagementApi } from '../../../services/planManagementApi';

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: User | null;
}

interface FormData {
  full_name: string;
  is_active: boolean;
  is_verified: boolean;
  subscription_plan: string;
  usage_limit_messages: number;
  request_rate_limit?: string;
}

interface FormErrors {
  full_name?: string;
  subscription_plan?: string;
  usage_limit_messages?: string;
}

export default function EditUserModal({ isOpen, onClose, onSuccess, user }: EditUserModalProps) {
  const [formData, setFormData] = useState<FormData>({
    full_name: '',
    is_active: true,
    is_verified: false,
    subscription_plan: 'starter',
    usage_limit_messages: 1000
  });
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [planLimits, setPlanLimits] = useState<Record<string, number>>({
    // Sensible defaults if admin plans endpoint is unavailable
    starter: 1000,
    professional: 5000,
    business: 10000,
    enterprise: -1,
  });
  const [plansLoaded, setPlansLoaded] = useState(false);

  // Update form data when user changes
  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name || '',
        is_active: user.is_active,
        is_verified: user.is_verified,
        subscription_plan: user.subscription_plan,
        usage_limit_messages: user.usage_limit_messages,
        request_rate_limit: user.request_rate_limit || ''
      });
      setErrors({});
    }
  }, [user]);

  // Fetch active plans to derive message limits per plan
  useEffect(() => {
    const loadPlans = async () => {
      try {
        const plans = await planManagementApi.getAllPlans(true, false);
        // Build name → messages_limit map (lowercased names)
        const map: Record<string, number> = { ...planLimits };
        plans.forEach((p: any) => {
          if (p?.name) {
            const key = String(p.name).toLowerCase();
            if (typeof p.messages_limit === 'number') {
              map[key] = p.messages_limit;
            }
          }
        });
        setPlanLimits(map);
      } catch (e) {
        // Non-fatal: keep defaults
      } finally {
        setPlansLoaded(true);
      }
    };
    if (isOpen && !plansLoaded) loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Full name is required';
    }
    
    if (!formData.subscription_plan) {
      newErrors.subscription_plan = 'Subscription plan is required';
    }
    
    if (formData.usage_limit_messages < 0) {
      newErrors.usage_limit_messages = 'Usage limit cannot be negative';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || !validateForm()) return;
    
    setLoading(true);
    try {
      const updateData: UpdateUserRequest = {
        full_name: formData.full_name,
        is_active: formData.is_active,
        is_verified: formData.is_verified,
        subscription_plan: formData.subscription_plan,
        usage_limit_messages: formData.usage_limit_messages === -1 ? -1 : Math.max(0, formData.usage_limit_messages),
        request_rate_limit: (formData.request_rate_limit || '').trim() || null
      };
      
      await adminApi.updateUser(user.id, updateData);
      
      onSuccess();
      onClose();
    } catch (error: any) {
      setErrors({
        full_name: error.response?.data?.detail || 'Failed to update user'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }

    // Auto-adjust monthly limit when plan changes
    if (field === 'subscription_plan') {
      const key = String(value || '').toLowerCase();
      const limit = planLimits[key];
      if (typeof limit === 'number') {
        setFormData(prev => ({ ...prev, usage_limit_messages: limit }));
      }
    }
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Edit User</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* User Info */}
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <UserCircle className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">{user.email}</p>
              <p className="text-sm text-gray-500">
                {user.user_type.replace('_', ' ')} • Created {new Date(user.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Full Name */}
          <div>
            <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 mb-1">
              Full Name *
            </label>
            <input
              type="text"
              id="full_name"
              value={formData.full_name}
              onChange={(e) => handleChange('full_name', e.target.value)}
              className={`block w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.full_name ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Enter full name"
            />
            {errors.full_name && (
              <p className="mt-1 text-sm text-red-600">{errors.full_name}</p>
            )}
          </div>

          {/* Status Toggles */}
          <div className="space-y-3">
            {/* Active Status */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">
                Account Active
              </label>
              <button
                type="button"
                onClick={() => handleChange('is_active', !formData.is_active)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  formData.is_active ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    formData.is_active ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {/* Verified Status */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">
                Email Verified
              </label>
              <button
                type="button"
                onClick={() => handleChange('is_verified', !formData.is_verified)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  formData.is_verified ? 'bg-green-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    formData.is_verified ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Subscription Plan */}
          <div>
            <label htmlFor="subscription_plan" className="block text-sm font-medium text-gray-700 mb-1">
              Subscription Plan *
            </label>
            <select
              id="subscription_plan"
              value={formData.subscription_plan}
              onChange={(e) => handleChange('subscription_plan', e.target.value)}
              className={`block w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.subscription_plan ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="starter">Starter Plan</option>
              <option value="professional">Professional Plan</option>
              <option value="business">Business Plan</option>
              <option value="enterprise">Enterprise Plan</option>
            </select>
            {errors.subscription_plan && (
              <p className="mt-1 text-sm text-red-600">{errors.subscription_plan}</p>
            )}
          </div>

          {/* Usage Limit */}
          <div>
            <label htmlFor="usage_limit_messages" className="block text-sm font-medium text-gray-700 mb-1">
              Monthly Message Limit
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                id="usage_limit_messages"
                value={formData.usage_limit_messages === -1 ? '' : formData.usage_limit_messages}
                onChange={(e) => handleChange('usage_limit_messages', e.target.value ? parseInt(e.target.value) : 0)}
                className={`block w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.usage_limit_messages ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="1000"
                min="0"
              />
              <button
                type="button"
                onClick={() => handleChange('usage_limit_messages', -1)}
                className={`px-3 py-2 text-sm border rounded-lg transition-colors ${
                  formData.usage_limit_messages === -1
                    ? 'bg-blue-100 text-blue-700 border-blue-300'
                    : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'
                }`}
              >
                Unlimited
              </button>
            </div>
            {errors.usage_limit_messages && (
              <p className="mt-1 text-sm text-red-600">{errors.usage_limit_messages}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
          Current usage: {user.current_usage_messages} messages this month
        </p>
      </div>

          {/* Request Rate Limit */}
          <div>
            <label htmlFor="request_rate_limit" className="block text-sm font-medium text-gray-700 mb-1">
              Request Rate Limit
            </label>
            <input
              type="text"
              id="request_rate_limit"
              value={formData.request_rate_limit || ''}
              onChange={(e) => handleChange('request_rate_limit', e.target.value)}
              className={`block w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent border-gray-300`}
              placeholder="e.g. 120/minute (leave blank for default)"
            />
            <p className="mt-1 text-xs text-gray-500">
              Override default request rate limit for this user. Format examples: 60/minute, 100/second, 5000/day.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Updating...' : 'Update User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
